// FILE: client/src/screens/AdminCreditsFormScreen.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import Uploader from "../../components/images/Uploader";

import {
  createCreditBlock,
  getCreditDetails,
  updateCreditBlock,
  deleteCreditBlock,
} from "../../actions/creditActions";

import {
  CREDIT_CREATE_RESET,
  CREDIT_UPDATE_RESET,
  CREDIT_DELETE_RESET,
} from "../../constants/creditConstants";

export default function AdminCreditsFormScreen() {
  const { id } = useParams(); // undefined on /new
  const isEdit = Boolean(id);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const details = useSelector((s) => s.creditsDetails);
  const { loading: loadingDetails, error: errorDetails, block } = details;

  const created = useSelector((s) => s.creditsCreate);
  const {
    success: successCreate,
    block: createdBlock,
    error: errorCreate,
  } = created;

  const updated = useSelector((s) => s.creditsUpdate);
  const { success: successUpdate, error: errorUpdate } = updated;

  const deleted = useSelector((s) => s.creditsDelete);
  const { success: successDelete, error: errorDelete } = deleted;

  // form
  const [label, setLabel] = useState("A – C");
  const [order, setOrder] = useState(1);
  const [isPublished, setIsPublished] = useState(false);

  const [imageAlt, setImageAlt] = useState("");
  const [images, setImages] = useState([]); // uploader returns array; we use first

  // items as textarea (one per line: Title | Type)
  const [itemsText, setItemsText] = useState("");

  const itemsArray = useMemo(() => {
    return itemsText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => {
        const [title, type] = line.split("|").map((x) => (x || "").trim());
        return { title, type: type || "" };
      })
      .filter((x) => x.title);
  }, [itemsText]);

  useEffect(() => {
    if (isEdit) dispatch(getCreditDetails(id));
    else {
      dispatch({ type: CREDIT_CREATE_RESET });
      dispatch({ type: CREDIT_UPDATE_RESET });
      dispatch({ type: CREDIT_DELETE_RESET });
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && block) {
      setLabel(block.label || "A – C");
      setOrder(Number(block.order || 1));
      setIsPublished(Boolean(block.isPublished));

      const existing = block.image?.url
        ? [
            {
              url: block.image.url,
              public_id: block.image.public_id || "",
            },
          ]
        : [];
      setImages(existing);

      setImageAlt(block.image?.alt || "");
      setItemsText(
        (block.items || [])
          .map((it) => `${it.title}${it.type ? " | " + it.type : ""}`)
          .join("\n"),
      );
    }
  }, [isEdit, block]);

  useEffect(() => {
    if (successCreate && createdBlock?._id) {
      dispatch({ type: CREDIT_CREATE_RESET });
      navigate(`/admin/credits/${createdBlock._id}`);
    }
  }, [successCreate, createdBlock, dispatch, navigate]);

  useEffect(() => {
    if (successUpdate) {
      dispatch({ type: CREDIT_UPDATE_RESET });
      navigate("/admin/credits");
    }
  }, [successUpdate, dispatch, navigate]);

  useEffect(() => {
    if (successDelete) {
      dispatch({ type: CREDIT_DELETE_RESET });
      navigate("/admin/credits");
    }
  }, [successDelete, dispatch, navigate]);

  const submitHandler = (e) => {
    e.preventDefault();

    const first = images?.[0];
    if (!first?.url) {
      alert("Please upload an image for this credits block.");
      return;
    }

    const payload = {
      label,
      order: Number(order) || 0,
      isPublished,
      image: {
        url: first.url,
        public_id: first.public_id || "",
        alt: imageAlt || "",
      },
      items: itemsArray,
    };

    if (isEdit) dispatch(updateCreditBlock(id, payload));
    else dispatch(createCreditBlock(payload));
  };

  const deleteHandler = () => {
    if (!isEdit) return;
    if (window.confirm("Delete this block? (soft delete)")) {
      dispatch(deleteCreditBlock(id));
    }
  };

  const isDeletedFlag = Boolean(block?.isDeleted);

  return (
    <Container className="py-4">
      <Row className="align-items-center mb-3">
        <Col>
          <h1 className="mb-1">
            {isEdit ? "Edit Credits Block" : "Create Credits Block"}
          </h1>
          {isEdit && (
            <div className="d-flex gap-2 flex-wrap">
              <Badge bg={isDeletedFlag ? "secondary" : "dark"}>
                {isDeletedFlag ? "Deleted" : "Active"}
              </Badge>
              <Badge
                bg={isPublished ? "success" : "warning"}
                text={isPublished ? undefined : "dark"}
              >
                {isPublished ? "Published" : "Unpublished"}
              </Badge>
            </div>
          )}
        </Col>

        <Col md="auto" className="d-flex gap-2 flex-wrap">
          <Link to="/admin/credits" className="btn btn-light">
            Back
          </Link>
          {isEdit && (
            <Button
              variant="outline-danger"
              onClick={deleteHandler}
              disabled={isDeletedFlag}
            >
              Delete
            </Button>
          )}
        </Col>
      </Row>

      {loadingDetails && <Loader />}
      {errorDetails && <Message variant="danger">{errorDetails}</Message>}
      {errorCreate && <Message variant="danger">{errorCreate}</Message>}
      {errorUpdate && <Message variant="danger">{errorUpdate}</Message>}
      {errorDelete && <Message variant="danger">{errorDelete}</Message>}

      {(!isEdit || block) && (
        <Form onSubmit={submitHandler}>
          <Row className="g-4">
            <Col lg={7}>
              <Card className="shadow-sm">
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Label</Form.Label>
                    <Form.Control
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                    />
                    <div
                      className="text-muted mt-1"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Example: <b>A – C</b>, <b>D – F</b>, etc.
                    </div>
                  </Form.Group>

                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Order</Form.Label>
                        <Form.Control
                          type="number"
                          value={order}
                          onChange={(e) => setOrder(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      <Form.Check
                        type="checkbox"
                        label="Published"
                        checked={isPublished}
                        onChange={(e) => setIsPublished(e.target.checked)}
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Items</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={10}
                      value={itemsText}
                      onChange={(e) => setItemsText(e.target.value)}
                      placeholder={
                        "One per line:\nTitle | Type\n\nExample:\nAlien | Film\nAnything Goes | Theatre"
                      }
                    />
                  </Form.Group>

                  <div className="d-flex gap-2 flex-wrap">
                    <Button variant="dark" type="submit">
                      {isEdit ? "Save" : "Create"}
                    </Button>
                    <Link to="/admin/credits" className="btn btn-outline-dark">
                      Cancel
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="shadow-sm">
                <Card.Body>
                  <h5>Image</h5>

                  <Form.Group className="mb-3">
                    <Form.Label>Alt Text</Form.Label>
                    <Form.Control
                      value={imageAlt}
                      onChange={(e) => setImageAlt(e.target.value)}
                    />
                  </Form.Group>

                  <Uploader
                    folder="credits"
                    existingImages={images}
                    setImageUrls={(imgs) => setImages(imgs)}
                  />

                  {images?.[0]?.url && (
                    <div className="mt-3">
                      <img
                        src={images[0].url}
                        alt={imageAlt || "Preview"}
                        style={{
                          width: "100%",
                          borderRadius: 12,
                          border: "1px solid rgba(18,18,18,0.12)",
                        }}
                      />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      )}
    </Container>
  );
}
